import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import { addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AbsenceForm from './AbsenceForm';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useAbsences } from '../../contexts/AbsencesContext';
import { useVisibilityPermissions } from '../../hooks/useVisibilityPermissions';
// TODO: Migrer vers les contextes API
// import { absencesDB, techniciansDB } from '../../db/database';
// import db from '../../db/database';
import { useSidebar } from '../../contexts/SidebarContext';
import { ABSENCE_TYPES } from '../../constants/absenceTypes';

const AbsenceCalendar = ({ onEdit }) => {
  const calendarRef = useRef(null);
  const { sidebarOpen } = useSidebar();
  const { technicians } = useTechnicians();
  const { absences, deleteAbsence } = useAbsences();
  const { canEdit, canDelete } = useVisibilityPermissions();
  const [events, setEvents] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [error, setError] = useState(null);
  const [initialDate, setInitialDate] = useState(() => {
    // Récupérer la date sauvegardée depuis localStorage
    const savedDate = localStorage.getItem('calendarDate');
    return savedDate || new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    loadData();
  }, [absences, technicians]);

  useEffect(() => {
    // Forcer le redimensionnement du calendrier quand la sidebar change
    const timer = setTimeout(() => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.updateSize();
      }
    }, 300); // Attendre la fin de la transition de la sidebar

    return () => clearTimeout(timer);
  }, [sidebarOpen]);

  const loadData = () => {
    try {
      const techniciansMap = technicians.reduce((acc, tech) => {
        acc[tech.id] = tech;
        return acc;
      }, {});

      const formattedEvents = absences.map(absence => {
        const technician = techniciansMap[absence.technicianId];
        if (!technician) return null;

        const absenceType = ABSENCE_TYPES[absence.type] || ABSENCE_TYPES.AUTRE;

        return {
          id: absence.id,
          title: `${technician.firstName} ${technician.lastName}`,
          start: absence.startDate,
          end: addDays(new Date(absence.endDate), 1).toISOString(),
          backgroundColor: absenceType.color,
          borderColor: '#e0e0e0',
          textColor: '#000',
          allDay: true,
          extendedProps: {
            type: absence.type,
            comment: absence.comment,
            absence: absence,
            typeLabel: absenceType.label
          }
        };
      }).filter(Boolean);

      setEvents(formattedEvents);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
  };

  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    
    // Si l'utilisateur ne peut ni éditer ni supprimer, ne pas afficher le menu contextuel
    if (!canEdit('absence') && !canDelete('absence')) {
      return;
    }
    
    setSelectedEvent(info.event);
    setContextMenu({
      mouseX: info.jsEvent.clientX,
      mouseY: info.jsEvent.clientY
    });
  };

  const handleCloseMenu = () => {
    setContextMenu(null);
  };

  const handleEdit = () => {
    handleCloseMenu();
    if (selectedEvent) {
      onEdit(selectedEvent.extendedProps.absence);
    }
  };

  const handleDeleteClick = () => {
    handleCloseMenu();
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedEvent) {
      try {
        const absenceId = selectedEvent.extendedProps.absence.id;
        console.log('Tentative de suppression de l\'absence avec ID:', absenceId);
        await deleteAbsence(absenceId);
        console.log('Absence supprimée avec succès');
        await loadData();
        setDeleteConfirmOpen(false);
        setSelectedEvent(null);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Erreur lors de la suppression de l\'absence');
      }
    }
  };

  // Sauvegarder la date du calendrier quand elle change
  const handleDatesSet = (dateInfo) => {
    // Utiliser activeStart qui pointe vers la date réelle du mois affiché
    // Ou ajouter 15 jours à currentStart pour être sûr d'être dans le mois principal
    const viewStart = new Date(dateInfo.view.currentStart);
    viewStart.setDate(viewStart.getDate() + 15); // S'assurer d'être au milieu du mois principal
    const currentDate = viewStart.toISOString().split('T')[0];
    localStorage.setItem('calendarDate', currentDate);
  };

  return (
    <>
      <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, p: 1 }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, multiMonthPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={initialDate}
            locale={frLocale}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'multiMonthYear,multiMonthSixMonth,dayGridMonth'
            }}
            views={{
              multiMonthYear: {
                type: 'multiMonth',
                duration: { months: 12 },
                buttonText: 'Année'
              },
              multiMonthSixMonth: {
                type: 'multiMonth',
                duration: { months: 6 },
                buttonText: 'Semestre'
              }
            }}
            weekNumbers={true}
            weekNumberFormat={{ week: 'numeric' }}
            weekText="Semaine "
            events={events}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            height="auto"
            dayMaxEvents={4}
            eventDisplay="block"
            eventOverlap={false}
            slotEventOverlap={false}
            displayEventTime={false}
            eventContent={(arg) => {
              const event = arg.event;
              const absenceType = ABSENCE_TYPES[event.extendedProps.type] || ABSENCE_TYPES.AUTRE;
              return (
                <Box sx={{
                  p: '2px 4px',
                  fontSize: '0.85em',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  height: '20px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <Typography sx={{ 
                    fontWeight: 'bold',
                    fontSize: 'inherit',
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                    flexShrink: 0
                  }}>
                    {event.title}
                  </Typography>
                  <Typography sx={{ 
                    color: 'text.secondary', 
                    px: 0.5,
                    flexShrink: 0
                  }}>
                    -
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.8em',
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      backgroundColor: `${absenceType.color}20`,
                      px: 0.5,
                      py: '1px',
                      borderRadius: '2px',
                      border: `1px solid ${absenceType.color}40`,
                      flexShrink: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100px',
                      height: 'fit-content',
                      lineHeight: 1.2
                    }}
                  >
                    {event.extendedProps.typeLabel}
                  </Typography>
                </Box>
              );
            }}
          />
        </Box>
      </Paper>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {canEdit('absence') && <MenuItem onClick={handleEdit}>Modifier</MenuItem>}
        {canDelete('absence') && <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          Supprimer
        </MenuItem>}
      </Menu>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette absence ?
            {selectedEvent && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedEvent.title}
                  <br />
                  {`Du ${format(new Date(selectedEvent.start), 'dd MMMM yyyy', { locale: fr })} au ${format(new Date(selectedEvent.end), 'dd MMMM yyyy', { locale: fr })}`}
                </Typography>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
          {canDelete('absence') && <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Supprimer
          </Button>}
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={error !== null} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AbsenceCalendar; 